import { createClient } from "@supabase/supabase-js";
import { resetStripeClient, setStripePublicKey } from "./stripeClient";
import { captureError } from "./sentry";
import { getTenantSlugFromPath } from "../api/apiHelpers";
import { getTenantSlugFromHost } from "../api/hostResolver";
import { safeGetItem, safeSetItem } from "@mediatechgroup/app-version";
import { storageSafeLock } from "./supabaseAuthLock";

// `supabase` is the per-tenant client. It starts as a placeholder and
// gets replaced by initializeTenantSupabase() once the tenant slug is
// resolved and tenant config is fetched from the central DB.
//
// There's no REACT_APP_SUPABASE_URL/_ANON_KEY env var — every tenant
// has its own database whose URL+key live in the central
// `tenants` table, and consumers of `supabase` are tenant-scoped UI
// that runs after init resolves. Public/non-tenant code that needs
// DB access should use `parentSupabase` (below) instead.
const PLACEHOLDER_URL = "https://placeholder.invalid";
const PLACEHOLDER_KEY = "placeholder";

// The auth options every client here is created with. `lock` keeps a browser
// that denies the Web Locks API (an embed iframe with third-party storage
// blocked) from failing the client's start-up with an unhandled SecurityError
// (Sentry CLUB-CENTRAL-TENANT-5B / -4M); see utils/supabaseAuthLock.
export const AUTH_OPTIONS = {
  persistSession: true,
  autoRefreshToken: true,
  lock: storageSafeLock,
};

export let supabase = createClient(PLACEHOLDER_URL, PLACEHOLDER_KEY, {
  auth: AUTH_OPTIONS,
});

// Track whether the tenant-specific client has been initialized
let tenantClientReady = false;

// Export a getter function to always get the current supabase instance
// This is more reliable during hot module reloading
export const getSupabase = () => supabase;

// Check if the Supabase client has been switched to the tenant database
export const isTenantClientReady = () => tenantClientReady;

// Wait for tenant client initialization (with timeout)
export const waitForTenantClient = (timeoutMs = 10000): Promise<boolean> => {
  if (tenantClientReady) return Promise.resolve(true);
  return new Promise((resolve) => {
    const start = Date.now();
    const check = () => {
      if (tenantClientReady) return resolve(true);
      if (Date.now() - start > timeoutMs) return resolve(false);
      setTimeout(check, 100);
    };
    check();
  });
};

// 2️⃣ Parent Supabase client — for tenant config lookup.
// Required for tenant lookup; without these, tenant routes can't load
// their per-tenant client. Same placeholder treatment so the bundle
// boots — initializeTenantSupabase() will short-circuit and the user
// sees the public landing instead of a blank page.
const parentSupabaseUrl = process.env.REACT_APP_PARENT_SUPABASE_URL || "";
const parentSupabaseKey = process.env.REACT_APP_PARENT_SUPABASE_ANON_KEY || "";

if (!parentSupabaseUrl || !parentSupabaseKey) {
  // eslint-disable-next-line no-console
  console.error(
    "[supabaseClient] REACT_APP_PARENT_SUPABASE_URL/ANON_KEY missing at build time; tenant lookups will fail.",
  );
}

export const parentSupabase = createClient(
  parentSupabaseUrl || PLACEHOLDER_URL,
  parentSupabaseKey || PLACEHOLDER_KEY,
  { auth: AUTH_OPTIONS },
);

// localStorage key for cached tenant config
const TENANT_CONFIG_CACHE_KEY = 'tenantConfig';

// 3️⃣ Initialize tenant Supabase client dynamically
export async function initializeTenantSupabase() {
  const pathParts = window.location.pathname.split("/").filter(Boolean);
  // Canonical tenant-slug resolution (validates invalid literals, apex public
  // paths, and slug format). Returns null when the URL isn't a tenant page.
  // Falls back to the host on tenant subdomains (`<slug>.clubcentral.app`),
  // where the slug lives in the host rather than the path.
  const tenantId = getTenantSlugFromPath() || getTenantSlugFromHost();

  // Skip if no valid tenant slug in the URL or host
  if (!tenantId) {
    return;
  }

  // Detect embed/iframe contexts where localStorage may be blocked and network is flaky.
  // In these contexts, tenant config errors are typically transient and handled gracefully
  // (the embed still renders with the fallback public supabase client), so we should not
  // log them to Sentry as errors — they create noise without being actionable.
  const isEmbedRoute = pathParts[1] === "embed";
  const isIframe = (() => {
    try {
      return window.self !== window.top;
    } catch {
      // Cross-origin access blocked = definitely in an iframe
      return true;
    }
  })();
  const suppressSentryErrors = isEmbedRoute || isIframe;

  // Fetch tenant config from the backend bootstrap endpoint (5s timeout,
  // 1 retry on transient errors). One server call returns the tenant
  // supabase config + stripe pk — the browser no longer queries the
  // central DB at boot (mobile backend-first; a native app uses the
  // same endpoint). "not_found" (typo'd slug, marketing path) is a
  // normal no-tenant-matched case and is never retried or logged.
  const { fetchTenantBootstrap } = await import("../api/bootstrapAPI");
  let data: any = null;
  let error: any = null;
  for (let attempt = 0; attempt < 2; attempt++) {
    const result = await fetchTenantBootstrap(tenantId);
    if (result.status === "ok") {
      data = {
        supabase_url: result.data.supabase.url,
        supabase_anon_key: result.data.supabase.anonKey,
        stripe_public_key: result.data.stripe.publishableKey,
        name: result.data.tenant.name,
      };
      error = null;
      break;
    }
    if (result.status === "not_found") {
      // definitive "no such tenant" — don't retry
      data = null;
      error = null;
      break;
    }
    error = result.error;
    console.warn(`⚠️ Bootstrap attempt ${attempt + 1} failed for: ${tenantId}`);
    // Brief delay before retry (only on timeout/transient errors)
    if (attempt === 0) await new Promise(r => setTimeout(r, 1000));
  }

  // Fallback: use localStorage cached config from last successful visit
  if ((error || !data)) {
    try {
      const cached = safeGetItem(TENANT_CONFIG_CACHE_KEY);
      if (cached) {
        const parsedCache = JSON.parse(cached);
        // Only use cache if it's for the same tenant
        if (parsedCache._tenantId === tenantId && parsedCache.supabase_url && parsedCache.supabase_anon_key) {
          console.warn(`🔄 Using localStorage cached config for tenant: ${tenantId}`);
          data = parsedCache;
          error = null;
        }
      }
    } catch {
      // localStorage unavailable or corrupted, continue
    }
  }

  if (error || !data) {
    // "No such tenant" (data === null, no error from maybeSingle) is an
    // expected case — e.g. user typed a non-existent slug or hit a marketing
    // path. Only capture to Sentry for actual errors (timeouts, DB failures),
    // and only outside embed/iframe contexts where they're typically transient.
    const isNoTenant = !error && !data;
    if (isNoTenant || suppressSentryErrors) {
      console.warn(`⚠️ Tenant config unavailable for "${tenantId}" — falling back to public client`);
    } else {
      captureError(
        error || new Error(`Tenant config not found for "${tenantId}"`),
        { component: 'supabaseClient', operation: 'loadTenantConfig', tenantId: tenantId ?? undefined }
      );
    }
    return;
  }

  const { supabase_url, supabase_anon_key, stripe_public_key, name } = data;

  // Guard against tenant records with missing Supabase config (e.g. dev-tenant on production)
  if (!supabase_url || !supabase_anon_key) {
    if (suppressSentryErrors) {
      console.warn(`⚠️ Tenant "${tenantId}" has no supabase_url or anon_key (embed/iframe context)`);
    } else {
      captureError(new Error(`Tenant "${tenantId}" has no supabase_url or anon_key configured`), {
        component: 'supabaseClient', operation: 'loadTenantConfig', tenantId: tenantId ?? undefined,
      });
    }
    return;
  }

  // Replace the exported supabase client with the tenant one
  supabase = createClient(supabase_url, supabase_anon_key, {
    auth: AUTH_OPTIONS,
  });
  tenantClientReady = true;

  // ✅ Cache tenant config in localStorage for offline/outage resilience
  try {
    safeSetItem(TENANT_CONFIG_CACHE_KEY, JSON.stringify({
      _tenantId: tenantId,
      supabase_url,
      supabase_anon_key,
      stripe_public_key,
      name,
    }));
  } catch {
    // localStorage full or unavailable — non-critical
  }

  // ✅ Reset Stripe client to ensure fresh initialization with new key
  resetStripeClient();
  // ✅ Store Stripe public key in BOTH localStorage and the
  //    in-memory fallback. Embed iframes (and some privacy-mode
  //    browsers) silently no-op localStorage writes, in which case
  //    the in-memory copy is the only thing keeping
  //    getStripePromise() from timing out — Sentry issue
  //    CLUB-CENTRAL-TENANT-3N.
  setStripePublicKey(stripe_public_key || null);
  // ✅ Store name
  safeSetItem("clubName", name);
}
